import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Upload } from "lucide-react";
import { GalleryUploader } from "@/components/Gallery";
import { CG_DISTRICTS, CG_DISTRICT_LIST } from "@/lib/cgLocations";

export default function NewsFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    title: "", category: "", excerpt: "", content: "", image_url: "", video_url: "",
    gallery_images: [],
    author: "समाचार डेस्क", is_breaking: false, is_editorial: false, is_published: true, tags: "",
    district: "", block: "",
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    if (isEdit) {
      api.get(`/news/${id}`).then((r) => setForm({
        title: r.data.title, category: r.data.category, excerpt: r.data.excerpt || "",
        content: r.data.content || "", image_url: r.data.image_url || "", video_url: r.data.video_url || "",
        gallery_images: r.data.gallery_images || [],
        author: r.data.author || "समाचार डेस्क", is_breaking: !!r.data.is_breaking,
        is_editorial: !!r.data.is_editorial,
        is_published: r.data.is_published !== false, tags: (r.data.tags || []).join(", "),
        district: r.data.district || "", block: r.data.block || "",
      })).catch((e) => setLoadErr(formatError(e))).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success("तस्वीर अपलोड हो गयी");
    } catch(err) { toast.error(formatError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = { ...form, tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) };
    try {
      if (isEdit) { await api.put(`/news/${id}`, payload); toast.success("अपडेट हो गया"); }
      else { await api.post("/news", payload); toast.success("प्रकाशित हो गया"); }
      nav("/admin/news");
    } catch(err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="news-form-page" className="max-w-4xl">
      <Link to="/admin/news" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F172A] mb-4"><ArrowLeft className="w-4 h-4"/>वापस</Link>
      <h1 className="text-2xl font-bold mb-6">{isEdit ? "समाचार संपादित करें" : "नया समाचार"}</h1>
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-lg p-10 shadow-sm text-slate-500" data-testid="news-form-loading">फ़ॉर्म लोड हो रहा है…</div>
      ) : loadErr ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700" data-testid="news-form-error">
          समाचार लोड नहीं हो सका: {loadErr}
        </div>
      ) : (
      <form onSubmit={submit} className="space-y-5 bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div>
          <Label>शीर्षक</Label>
          <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required data-testid="input-title"/>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>श्रेणी</Label>
            <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
              <SelectTrigger data-testid="select-category"><SelectValue placeholder="चुनें"/></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>लेखक</Label>
            <Input value={form.author} onChange={(e) => setForm({...form, author: e.target.value})} data-testid="input-author"/>
          </div>
        </div>
        <div>
          <Label>सारांश (excerpt)</Label>
          <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} data-testid="input-excerpt"/>
        </div>
        <div>
          <Label>मुख्य सामग्री</Label>
          <Textarea rows={10} value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} required data-testid="input-content"/>
        </div>
        <div>
          <Label>तस्वीर</Label>
          <div className="flex items-center gap-3">
            <Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="URL या अपलोड करें" data-testid="input-image-url"/>
            <label className="cursor-pointer flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-2 text-sm hover:bg-slate-50">
              <Upload className="w-4 h-4"/> अपलोड
              <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="upload-image"/>
            </label>
          </div>
          {form.image_url && <img src={form.image_url} alt="" className="mt-3 h-40 rounded object-cover"/>}
        </div>
        <div>
          <Label>YouTube वीडियो URL (वैकल्पिक)</Label>
          <Input value={form.video_url} onChange={(e) => setForm({...form, video_url: e.target.value})} placeholder="https://youtu.be/... या https://www.youtube.com/watch?v=..." data-testid="input-video-url"/>
          <div className="text-xs text-slate-500 mt-1">वीडियो होने पर लेख पेज पर image की जगह प्लेयर दिखेगा</div>
        </div>
        <div>
          <Label>फोटो गैलरी (कई तस्वीरें)</Label>
          <GalleryUploader images={form.gallery_images} onChange={(g) => setForm({...form, gallery_images: g})}/>
        </div>
        <div>
          <Label>टैग (कॉमा से अलग)</Label>
          <Input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} placeholder="राजनीति, संसद" data-testid="input-tags"/>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>जिला (छत्तीसगढ़)</Label>
            <select value={form.district} onChange={(e) => setForm({...form, district: e.target.value, block: ""})}
              className="w-full h-10 px-3 rounded-md border border-slate-200" data-testid="input-district">
              <option value="">— सभी —</option>
              {CG_DISTRICT_LIST.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <Label>ब्लॉक / तहसील</Label>
            <select value={form.block} onChange={(e) => setForm({...form, block: e.target.value})}
              className="w-full h-10 px-3 rounded-md border border-slate-200" disabled={!form.district} data-testid="input-block">
              <option value="">— चुनें —</option>
              {(CG_DISTRICTS[form.district] || []).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-8 pt-2">
          <label className="flex items-center gap-2"><Switch checked={form.is_breaking} onCheckedChange={(v) => setForm({...form, is_breaking: v})} data-testid="switch-breaking"/> ब्रेकिंग</label>
          <label className="flex items-center gap-2"><Switch checked={form.is_editorial} onCheckedChange={(v) => setForm({...form, is_editorial: v})} data-testid="switch-editorial"/> प्रधान संपादक की कलम से</label>
          <label className="flex items-center gap-2"><Switch checked={form.is_published} onCheckedChange={(v) => setForm({...form, is_published: v})} data-testid="switch-published"/> प्रकाशित</label>
        </div>
        <div className="flex gap-3 pt-4 border-t border-slate-100">
          <Button type="submit" className="bg-[#0F172A] hover:bg-[#1E3A8A]" disabled={busy} data-testid="submit-news">{busy ? "सहेज रहे…" : (isEdit ? "अपडेट करें" : "प्रकाशित करें")}</Button>
          <Button type="button" variant="outline" onClick={() => nav("/admin/news")}>रद्द करें</Button>
        </div>
      </form>
      )}
    </div>
  );
}
